import { createSelector } from 'reselect';

import {
    EpgProgram,
    RBChannel,
    RBLink,
    SearchLink,
} from 'store/railsBuilder/reducer.types';
import { selectCurrentTime } from 'store/timer/selectors';
import { FavouriteChannelsListMetadataItem } from 'store/user/reducer.types';
import {
    selectActiveFavouriteChannelsList,
    selectEntitlements,
    selectFavouriteChannelsList,
} from 'store/user/selectors';

import isCUEndDateValid from 'services/data-manager/catchup/isCUEndDateValid';

import isSubscribedToChannel from 'utils/isSubscribedToChannel';
import isValidDeeplinkItem from 'utils/isValidDeeplinkItem';
import isValidPastDeeplinkItem from 'utils/isValidPastDeeplinkItem';

import { ReduxStore } from '../types';

export function selectProgramsState(store: ReduxStore) {
    return store.programs;
}

export function getChannels(store: ReduxStore) {
    return store.programs.channels;
}

export const selectChannels = createSelector(
    getChannels,
    selectActiveFavouriteChannelsList,
    selectEntitlements,
    (channels, favouritesChannels, entitlements) => {
        if (channels) {
            if (favouritesChannels && favouritesChannels.metadata) {
                const channelsById = channels.reduce<
                    Record<RBChannel['editorial']['id'], RBChannel>
                >(
                    (acc, channel) => ({
                        ...acc,
                        [channel.editorial.id]: channel,
                    }),
                    {},
                );
                const favouriteChannelsList: FavouriteChannelsListMetadataItem[] =
                    Object.keys(favouritesChannels.metadata).map((key) =>
                        JSON.parse(favouritesChannels.metadata[key]),
                    );

                return favouriteChannelsList
                    .sort(({ sortIndex: a }, { sortIndex: b }) => a - b)
                    .reduce<RBChannel[]>((acc, { channelId, isHidden }) => {
                        const requiredChannel = channelsById[channelId];

                        if (requiredChannel) {
                            const isSubscribedChannel = isSubscribedToChannel(
                                requiredChannel,
                                entitlements,
                            );
                            const isChannelVisible =
                                favouritesChannels.tags?.includes(
                                    'unsubscribed_hidden',
                                )
                                    ? isSubscribedChannel && !isHidden
                                    : !isHidden;

                            if (isChannelVisible) {
                                return [...acc, requiredChannel];
                            }
                        }

                        return acc;
                    }, []);
            }

            return channels;
        }

        return [];
    },
);

export const selectFavoriteChannels = createSelector(
    getChannels,
    selectFavouriteChannelsList,
    (channels, favouritesChannels) => {
        if (channels) {
            if (favouritesChannels && favouritesChannels.metadata) {
                const favouriteChannelsList: FavouriteChannelsListMetadataItem[] =
                    Object.keys(favouritesChannels.metadata).map((key) =>
                        JSON.parse(favouritesChannels.metadata[key]),
                    );

                const map = new Map();
                favouriteChannelsList.forEach(({ channelId, sortIndex }) =>
                    map.set(channelId, sortIndex),
                );

                return [...channels].sort((x, y) =>
                    map.get(x.editorial.id)
                        ? map.get(x.editorial.id) - map.get(y.editorial.id)
                        : -1,
                );
            }

            return channels;
        }

        return [];
    },
);

export const selectChannelsById = createSelector(getChannels, (channels) =>
    channels
        ? channels.reduce<Record<RBChannel['editorial']['id'], RBChannel>>(
              (acc, channel) => ({
                  ...acc,
                  [channel.editorial.id]: channel,
              }),
              {},
          )
        : {},
);

export const selectChannelsListWithTvChannel = createSelector(
    selectChannels,
    (channels) =>
        channels.filter(({ editorial: { tvChannel } }) => !!tvChannel),
);

export const selectChannelById = createSelector(
    [selectChannels, (state: ReduxStore, channelId?: string) => channelId],
    (channels, channelId?: string) => {
        return channels.find(
            (channelItem) => channelItem?.editorial?.id === channelId,
        );
    },
);

export const selectStandardChannelById = createSelector(
    [getChannels, (_: ReduxStore, channelId?: string) => channelId],
    (channels, channelId?: string) => {
        return channels?.find(
            (channelItem) => channelItem?.editorial?.id === channelId,
        );
    },
);

function getChannelsRequest(store: ReduxStore) {
    return store.programs.getChannelsRequest;
}

export const selectGetChannelsRequestStatus = createSelector(
    getChannelsRequest,
    (request) => request.status,
);

export const selectCatchupIconValid = createSelector(
    [
        selectCurrentTime,
        (state: ReduxStore, endTime?: number) => endTime,
        (state: ReduxStore, endTime?: number, CUEndDate?: number) => CUEndDate,
    ],
    (_?: number, endTime?: number, CUEndDate?: number) => {
        return isCUEndDateValid(endTime, CUEndDate);
    },
);

export const selectDeeplinkValid = createSelector(
    [
        selectCurrentTime,
        (state: ReduxStore, link?: RBLink | SearchLink) => link,
    ],
    (_?: number, link?: RBLink | SearchLink): boolean =>
        isValidDeeplinkItem(link),
);

export const selectIsValidPastDeeplink = createSelector(
    [selectCurrentTime, (state: ReduxStore, item?: EpgProgram) => item],
    (_?: number, item?: EpgProgram): boolean | undefined =>
        isValidPastDeeplinkItem(item),
);

export function selectNowNextByChannelId(store: ReduxStore) {
    return store.programs.nowNextByChannelId;
}
